import { useState } from 'react'
import { savedCreditCardApis } from '../Services/Apis'
import { get } from 'lodash'
import { CreditCardHelper } from 'Helpers/CreditCardHelper'
import { runInAction } from 'mobx'
import storeCheckout from 'GlobalStores/Checkout/StoreCheckout'
import storeUser from 'GlobalStores/User/StoreUser'
import { storeCreditCardAlias } from '../Elements/CreditCardAliasBox'
import storeCountry from 'GlobalStores/Cores/County/StoreCountry'

export function useCreditcardAliases() {
    const [savedCreditCards, setSavedCreditCards] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [apiSaveResponse, setApiSaveResponse] = useState(null)
    const [retryCount, setRetryCount] = useState(0)
    const [isNewCard, setIsNewCard] = useState(false)

    const [creditCard, setCreditCard] = useState({
        card_number: '',
        exp_date: '',
        name_on_card: '',
        cvv: ''
    })

    const fetchSavedCreditCards = async callback => {
        try {
            if (loading) return
            setLoading(true)
            const response = await savedCreditCardApis.getSavedCreditCards({ userCountry: storeUser.CustomerData().MainAddress().country })
     
            setSavedCreditCards(response.data)
            runInAction(() => {
                storeCheckout.savedCardAlias = response.data
            })
            callback && callback(response.data)
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    const deleteCreditCard = async ({ number, callback }) => {
        try {
            setLoading(true)
            await savedCreditCardApis.deleteSavedCreditCard({ cardAlias: number, userCountry: storeUser.CustomerData().MainAddress().country })
            callback && callback(true)
            setLoading(false)
        } catch (error) {
            callback && callback(false, get(error, 'response.data.error.error_message', []))
            setLoading(false)
        }
    }

    const handleSave = async ({ cardNumber, expiryDate, nameOnCard, cvv, callback, userCountry }) => {
        setLoading(true)
        setError(null)
        try {

            const validateCard = await CreditCardHelper.cardValidate({
                countryCode: storeCountry.Country2(),
                creditCard: {
                    creditCardNumber: cardNumber,
                    creditCardExpires: expiryDate,
                    creditCardSecurityCode: cvv,
                    payer: nameOnCard
                }
            })

            if (!validateCard.success) {
                setError(validateCard.error_messages)
                return
            }

            const methodDetails = {
                methodDetails: {
                    alias: cardNumber.slice(0, 5) + 'xxxxxx' + cardNumber.slice(-4),
                    creditCardNumber: cardNumber,
                    creditCardExpires: `${Number(expiryDate.split('/')[1]) + 2000}-${expiryDate.split('/')[0]}`,
                    payer: nameOnCard,
                    billToAddress: {
                        country: userCountry
                    }
                },
                billToAddress: {
                    country: userCountry
                }
            }
            const response = await savedCreditCardApis.saveCreditCard({ methodDetails, userCountry: userCountry })
            setApiSaveResponse(response)
            setCreditCard({
                card_number: cardNumber,
                exp_date: expiryDate,
                name_on_card: nameOnCard,
                cvv: cvv
            })
            setIsNewCard(true)
            callback && callback()
        } catch (error) {
            console.log('useCreditcardAliases error:', error)
            console.log('SaveCardAlias error:', error)

            storeCreditCardAlias.setRetryCount(storeCreditCardAlias.retryCount + 1)
            if (storeCreditCardAlias.retryCount < storeCreditCardAlias.remainingTime) {
                // Retry the save operation
                return await handleSave({ cardNumber, expiryDate, nameOnCard, cvv, callback, userCountry })
            } else {
                // Max retries reached, throw error to stop execution
                setError('failed_to_save_card_alias')
                throw new Error('Failed to save card alias after maximum retries.', storeCreditCardAlias.retryCount)
            }
        } finally {
            setLoading(false)
        }
    }

    const getRemainingTime = async () => {
        try {
            const response = await savedCreditCardApis.getRemainingTime()
            return Number(response.time) || 1
        } catch (e) {
            return 1
        }
    }

    const GetCardAliasWithRetry = async alias => {
        const remainingTime = await getRemainingTime()
        // console.log('get card alias with retry')
        return new Promise((resolve, reject) => {
            let cardAlias = null
            const interval = setInterval(async () => {
                if (retryCount >= remainingTime) {
                    clearInterval(interval)
                    resolve(alias)
                }

                const getCreditCardAlias = await savedCreditCardApis.getSavedCreditCards({ userCountry: storeUser.CustomerData().MainAddress().country })
                const findCreditCardAlias = getCreditCardAlias.data.find(
                    item =>
                        item.methodDetails.alias === alias ||
                        item.methodDetails.creditCardNumber === alias ||
                        item.methodDetails.creditCardNumberMasked === alias
                )

                if (findCreditCardAlias) {
                    clearInterval(interval)
                    cardAlias = findCreditCardAlias.methodDetails.alias
                    // console.log('found it')
                    resolve(cardAlias)
                }
                setRetryCount(retryCount + 1)
            }, 1000)
        })
    }

    return {
        savedCreditCards,
        loading,
        fetchSavedCreditCards,
        handleSave,
        error,
        creditCard,
        setCreditCard,
        deleteCreditCard,
        apiSaveResponse,
        GetCardAliasWithRetry,
        getRemainingTime,
        isNewCard
    }
}
